import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

// Material UI & Icons
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Avatar,
  Stack,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
  TextareaAutosize,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import { TbExternalLink } from "react-icons/tb";
import {
  Search,
  Groups,
  CloudUpload,
  Archive,
  DeleteSweep,
  Refresh,
  CheckCircleRounded,
  HourglassTopRounded,
  CancelRounded,
  FilterList,
  FilterListOff,
  PersonSearch,
  ContentCopyOutlined,
} from "@mui/icons-material";

// Actions & Components
import { TeamTableAction } from "../../../Redux/action/TeamTableAction";
import { VendorProfile as VendorProfileAction } from "../../../Redux/action/VendorProfileAction";
import DeactivateModal from "../../Common/DeactivateMemberModal";
import Pagination from "../../Common/Pagination/Pagination";
import ImportCandidates from "../ImportCandidates";
import ArchivedMembers from "./ArchivedMembers";
import RequestedProfile from "./RequestedProfile";
import { fetchSkillDetails } from "../../../Redux/action/MasterSkillAction";

// Custom Hooks
import { useCountries } from "../../../Hooks/useCountries";
import { useStatesByCountry } from "../../../Hooks/useStatesByCountry";
import Loader from "../../Common/Loader/loader";

const TeamListV2 = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // PRIMARY THEME COLORS
  const PRIMARY_COLOR = "#2d6d96";
  const PRIMARY_GRADIENT = "linear-gradient(135deg, #2d6d96 0%, #1a4a6b 100%)";

  // --- 1. STATE MANAGEMENT ---
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [showOtherTextarea, setShowOtherTextarea] = useState(false);

  const [filters, setFilters] = useState({
    skills: [],
    countries: [],
    states: [],
    visaTypes: [],
  });

  // --- 2. DATA SELECTORS & DEFINITIONS ---
  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector(
    (state) => state?.TeamTable?.result?.content || [],
  );
  const totalElements = useSelector(
    (state) => state?.TeamTable?.result?.totalElements || 0,
  );
  const totalPages = useSelector(
    (state) => state?.TeamTable?.result?.totalPages,
  );
  const isDataLoading = useSelector((state) => state?.TeamTable?.isLoading);
  const AllSkillList = useSelector(
    (state) => state?.MasterTableDetails?.skill?.content || [],
  );
  const baseUrl = process.env.REACT_APP_API_PATH;

  const queryParams = useMemo(
    () => new URLSearchParams(location?.search),
    [location.search],
  );
  const vId = queryParams.get("vendorId");
  const vendorid = vId || userResult?.vendorid;
  const currentTab = queryParams.get("active") || "teamMember";

  const skillOptions = useMemo(
    () => AllSkillList.map((s) => s.skillName || s),
    [AllSkillList],
  );

  // --- 3. FILTER LOGIC ---
  const filteredData = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    return userInfo.filter((item) => {
      const firstName = item?.firstName?.toLowerCase() || "";
      const lastName = item?.lastName?.toLowerCase() || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const itemCountry = item?.country?.toLowerCase() || "";
      const itemState = item?.state?.toLowerCase() || "";

      const itemSkills = Array.isArray(item?.skillList)
        ? item.skillList
            .map((s) =>
              typeof s === "string"
                ? s.toLowerCase()
                : s?.skillName?.toLowerCase(),
            )
            .filter(Boolean)
        : [];

      const matchesSearch = !search || fullName.includes(search);
      const matchesSkills =
        filters.skills.length === 0 ||
        filters.skills.some((skill) =>
          itemSkills.includes(skill.toLowerCase()),
        );
      const matchesCountry =
        filters.countries.length === 0 ||
        filters.countries.some(
          (c) => (c?.country || c || "").toLowerCase() === itemCountry,
        );
      const matchesState =
        filters.states.length === 0 ||
        filters.states.some(
          (s) => (s?.name || s || "").toLowerCase() === itemState,
        );

      return matchesSearch && matchesSkills && matchesCountry && matchesState;
    });
  }, [userInfo, searchValue, filters]);

  const { data: countriesData = [] } = useCountries();
  const lastCountry = filters.countries[filters.countries.length - 1];
  const { data: stateList = [] } = useStatesByCountry(
    lastCountry?.country || lastCountry || "India",
  );

  useEffect(() => {
    dispatch(VendorProfileAction(vendorid));
    dispatch(fetchSkillDetails());
  }, [dispatch, vendorid]);

  useEffect(() => {
    if (currentTab === "teamMember") {
      dispatch(
        TeamTableAction(vendorid, "", "", pageNumber, location.pathname),
      );
    }
  }, [dispatch, vendorid, pageNumber, currentTab, location.pathname]);

  // --- 4. HANDLERS ---
  const handleSelectAll = (e) =>
    setSelectedCandidates(
      e.target.checked ? filteredData.map((i) => i.candidateId) : [],
    );
  const handleCandidateSelection = (e, id) =>
    setSelectedCandidates((prev) =>
      e.target.checked ? [...prev, id] : prev.filter((p) => p !== id),
    );

  const handleDeactivateSubmit = async () => {
    if (!selectedReason) return toast.error("Please select a reason");
    setLoading(true);
    try {
      const content = {
        reason: selectedReason === "other" ? otherReason : selectedReason,
        candidateId: selectedCandidates,
        vendorId: vendorid,
      };
      await axios.put(`${baseUrl}/ebench/deactivate_team_member`, content);
      toast.success("Accounts deactivated");
      setSelectedCandidates([]);
      setIsModalOpen(false);
      dispatch(
        TeamTableAction(vendorid, "", "", pageNumber, location.pathname),
      );
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setLoading(false);
    }
  };

  const getStatusDetails = (status) => {
    const s = status?.toUpperCase();
    if (s === "COMPLETE")
      return {
        label: "Completed",
        color: "#047857",
        bg: "#ECFDF5",
        icon: <CheckCircleRounded sx={{ fontSize: 16 }} />,
      };
    if (s === "PARTIALLY_COMPLETE" || s === "PARTIAL")
      return {
        label: "Partial",
        color: "#B45309",
        bg: "#FFFBEB",
        icon: <HourglassTopRounded sx={{ fontSize: 16 }} />,
      };
    return {
      label: "Incomplete",
      color: "#B91C1C",
      bg: "#FEF2F2",
      icon: <CancelRounded sx={{ fontSize: 16 }} />,
    };
  };

  return (
    <Box sx={{ bgcolor: "#F8FAFC", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "#205578",
              letterSpacing: "-0.4px",
              lineHeight: 1.2,
            }}
          >
            Team Management
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#64748B",
              fontWeight: 500,
              mt: 0.5,
              maxWidth: 520,
            }}
          >
            Efficiently manage and optimize your team and bench strength.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{ width: { xs: "100%", md: "auto" } }}
        >
          <Button
            variant="outlined"
            startIcon={showFilters ? <FilterListOff /> : <FilterList />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              color: PRIMARY_COLOR,
              borderColor: "#E2E8F0",
              bgcolor: "#fff",
              "&:hover": { borderColor: PRIMARY_COLOR, bgcolor: "#F0F9FF" },
            }}
          >
            {showFilters ? "Hide Filters" : "Filters"}
          </Button>

          {selectedCandidates.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweep />}
              onClick={() => setIsModalOpen(true)}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.25)",
              }}
            >
              Deactivate ({selectedCandidates.length})
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {showFilters && (
          <Grid item xs={12} lg={2.5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "20px",
                border: "1px solid #E5E7EB",
                position: "sticky",
                top: 24,
                bgcolor: "#FFFFFF",
                boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px dashed #E5E7EB",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 900,
                    color: PRIMARY_COLOR,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    letterSpacing: "0.3px",
                  }}
                >
                  <Search sx={{ fontSize: 20 }} />
                  Refine Search
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", mt: 0.5, display: "block" }}
                >
                  Narrow results using filters
                </Typography>
              </Box>

              {/* Filters */}
              <Stack spacing={2.5}>
                {/* Search */}
                <TextField
                  fullWidth
                  placeholder="Search by name"
                  size="small"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E5E7EB",
                      },
                      "&:hover fieldset": {
                        borderColor: PRIMARY_COLOR,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: PRIMARY_COLOR,
                        borderWidth: 2,
                      },
                    },
                  }}
                />

                {/* Country */}
                <Autocomplete
                  multiple
                  options={countriesData?.data || []}
                  getOptionLabel={(o) => o.country || o}
                  value={filters.countries}
                  onChange={(e, v) => setFilters({ ...filters, countries: v })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F8FAFC",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: PRIMARY_COLOR,
                          borderWidth: 2,
                        },
                      }}
                    />
                  )}
                />

                {/* State */}
                <Autocomplete
                  multiple
                  options={stateList?.data?.states || []}
                  getOptionLabel={(o) => o.name || o}
                  value={filters.states}
                  onChange={(e, v) => setFilters({ ...filters, states: v })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F8FAFC",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: PRIMARY_COLOR,
                          borderWidth: 2,
                        },
                      }}
                    />
                  )}
                />

                {/* Skills */}
                <Autocomplete
                  multiple
                  options={skillOptions}
                  value={filters.skills}
                  onChange={(e, v) => setFilters({ ...filters, skills: v })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Skills"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          bgcolor: "#F8FAFC",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                          borderColor: PRIMARY_COLOR,
                          borderWidth: 2,
                        },
                      }}
                    />
                  )}
                />

                {/* Reset */}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={() =>
                    setFilters({
                      skills: [],
                      countries: [],
                      states: [],
                      visaTypes: [],
                    })
                  }
                  sx={{
                    mt: 1,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 800,
                    bgcolor: "#F1F5F9",
                    color: PRIMARY_COLOR,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#E0ECFF",
                      boxShadow: "none",
                    },
                  }}
                >
                  Reset Filters
                </Button>
              </Stack>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} lg={showFilters ? 9.5 : 12}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              bgcolor: "#fff",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                p: 1.5,
                display: "flex",
                gap: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
                borderBottom: "1px solid #F1F5F9",
                bgcolor: "#F8FAFC",
              }}
            >
              {[
                {
                  id: "teamMember",
                  label: "Active",
                  icon: <Groups fontSize="small" />,
                },
                {
                  id: "req",
                  label: "Requested",
                  icon: <PersonSearch fontSize="small" />,
                },
                {
                  id: "import",
                  label: "Import",
                  icon: <CloudUpload fontSize="small" />,
                },
                {
                  id: "archived",
                  label: "Closed",
                  icon: <Archive fontSize="small" />,
                },
              ].map((t) => (
                <Link
                  key={t.id}
                  to={`/team-list?active=${t.id}${vId ? `&vendorId=${vId}` : ""}`}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    startIcon={t.icon}
                    sx={{
                      px: { xs: 1.5, md: 3 },
                      py: 0.8,
                      minWidth: { xs: 110, md: "auto" },
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "10px",
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      justifyContent: "center",
                      bgcolor:
                        currentTab === t.id ? PRIMARY_COLOR : "transparent",
                      color: currentTab === t.id ? "#fff" : "#64748B",
                      backgroundImage:
                        currentTab === t.id ? PRIMARY_GRADIENT : "none",
                      "&:hover": {
                        bgcolor:
                          currentTab === t.id ? PRIMARY_COLOR : "#F1F5F9",
                      },
                    }}
                  >
                    {t.label}
                  </Button>
                </Link>
              ))}
            </Box>
            <Divider />

            {currentTab === "teamMember" ? (
              <Box>
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    bgcolor: "#fff",
                    borderBottom: "1px solid #F1F5F9",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 800, color: "#475569" }}
                  >
                    Showing {filteredData.length} team members
                  </Typography>
                </Box>

                <TableContainer>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead
                      sx={{ bgcolor: "#F8FAFC", whiteSpace: "nowrap" }}
                    >
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              filteredData.length > 0 &&
                              selectedCandidates.length === filteredData.length
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "#334155",
                            textAlign: "left",
                          }}
                        >
                          Name & ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155" }}>
                          Designation
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155" }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155" }}>
                          Location
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: "#334155" }}>
                          Completion Status
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: "#334155" }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isDataLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                            <Loader />
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredData.map((item, i) => {
                          const status = getStatusDetails(
                            item.profileCompleteness,
                          );
                          return (
                            <TableRow
                              key={i}
                              hover
                              sx={{ "&:hover": { bgcolor: "#F1F5F9" } }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedCandidates.includes(
                                    item.candidateId,
                                  )}
                                  onChange={(e) =>
                                    handleCandidateSelection(
                                      e,
                                      item.candidateId,
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  alignItems="center"
                                >
                                  <Avatar
                                    src={item.profileImageUrl}
                                    sx={{
                                      width: 55,
                                      height: 55,
                                      border: "2px solid #E2E8F0",
                                    }}
                                  >
                                    {item.firstName?.[0]}
                                  </Avatar>
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        color: "#020617",
                                        mb: "2px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {item.firstName} {item.lastName}
                                    </Typography>

                                    <Box
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        px: 1,
                                        py: "2px",
                                        borderRadius: "999px",
                                        bgcolor: "#EFF6FF",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: PRIMARY_COLOR,
                                          fontWeight: 700,
                                          letterSpacing: "0.4px",
                                          whiteSpace: "nowrap",
                                          padding: "2px 8px",
                                          borderRadius: "999px",
                                          backgroundColor: `${PRIMARY_COLOR}12`,
                                          boxShadow: `0 3px 8px ${PRIMARY_COLOR}40`,
                                        }}
                                      >
                                        ID: {item.candidateEmpId}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell
                                sx={{ color: "#475569", fontWeight: 500 }}
                              >
                                {item.currentDesignation || "—"}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 240 }}>
                                {item.email ? (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{
                                      "&:hover .copy-icon": {
                                        opacity: 1,
                                      },
                                    }}
                                  >
                                    <Tooltip title={item.email} arrow>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: "#475569",
                                          fontWeight: 500,
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          cursor: "pointer",
                                        }}
                                      >
                                        {item.email}
                                      </Typography>
                                    </Tooltip>

                                    <Tooltip title="Copy email" arrow>
                                      <IconButton
                                        size="small"
                                        className="copy-icon"
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            item.email,
                                          );
                                          toast.success("Email copied");
                                        }}
                                        sx={{
                                          opacity: 0,
                                          transition: "opacity 0.2s ease",
                                          color: "#94A3B8",
                                          "&:hover": {
                                            color: PRIMARY_COLOR,
                                          },
                                        }}
                                      >
                                        <ContentCopyOutlined
                                          sx={{ fontSize: 16 }}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ) : (
                                  "—"
                                )}
                              </TableCell>

                              <TableCell
                                sx={{ color: "#475569", fontSize: "0.85rem" }}
                              >
                                {item.city || item.state || item.country
                                  ? [item.city, item.state, item.country]
                                      .filter(Boolean)
                                      .join(", ")
                                  : "-"}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={status.icon}
                                  label={status.label}
                                  size="small"
                                  sx={{
                                    bgcolor: status.bg,
                                    color: status.color,
                                    fontWeight: 500,
                                    borderRadius: "6px",
                                    "& .MuiChip-icon": { color: status.color },
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  justifyContent="flex-end"
                                >
                                  <Tooltip title="View Profile">
                                    <IconButton
                                      component={Link}
                                      to={`/view_candidate/${item.candidateId}`}
                                      size="small"
                                      sx={{
                                        color: PRIMARY_COLOR,
                                        "&:hover": { bgcolor: "#E0F2FE" },
                                      }}
                                    >
                                      <TbExternalLink />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit Member">
                                    <IconButton
                                      component={Link}
                                      to={`/update-team/${item.candidateId}`}
                                      size="small"
                                      sx={{
                                        color: PRIMARY_COLOR,
                                        "&:hover": { bgcolor: "#E0F2FE" },
                                      }}
                                    >
                                      <CiEdit />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      justifyContent: "center",
                      borderTop: "1px solid #F1F5F9",
                    }}
                  >
                    <Pagination
                      totalPages={totalPages}
                      currentPage={pageNumber}
                      onPageChange={setPageNumber}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                {currentTab === "req" && (
                  <RequestedProfile activeTab={currentTab} />
                )}
                {currentTab === "archived" && (
                  <ArchivedMembers activeTab={currentTab} />
                )}
                {currentTab === "import" && (
                  <ImportCandidates activeTab={currentTab} />
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* MODAL SECTION */}
      <DeactivateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        heading="Deactivate Team Member"
      >
        <FormControl fullWidth sx={{ p: 1 }}>
          <FormLabel sx={{ fontWeight: 800, mb: 2, color: "#334155" }}>
            Reason for deactivation <span style={{ color: "#EF4444" }}>*</span>
          </FormLabel>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => {
              setSelectedReason(e.target.value);
              setShowOtherTextarea(e.target.value === "other");
            }}
          >
            {["Terminated", "Contract Ended", "Personal Reasons", "other"].map(
              (r) => (
                <FormControlLabel
                  key={r}
                  value={r}
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: PRIMARY_COLOR,
                        "&.Mui-checked": { color: PRIMARY_COLOR },
                      }}
                    />
                  }
                  label={r}
                />
              ),
            )}
          </RadioGroup>
          {showOtherTextarea && (
            <TextareaAutosize
              minRows={4}
              placeholder="Please provide additional details..."
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              style={{
                width: "100%",
                marginTop: "15px",
                padding: "12px",
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                fontFamily: "inherit",
                outline: "none",
              }}
            />
          )}
          <Box
            sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "flex-end" }}
          >
            <Button
              onClick={() => setIsModalOpen(false)}
              sx={{ color: "#64748B", textTransform: "none", fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeactivateSubmit}
              disabled={loading}
              sx={{
                bgcolor: PRIMARY_COLOR,
                backgroundImage: PRIMARY_GRADIENT,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 4,
                "&:hover": { opacity: 0.9 },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Confirm Archive"
              )}
            </Button>
          </Box>
        </FormControl>
      </DeactivateModal>
    </Box>
  );
};

export default TeamListV2;
